import type { QualificationKey, RunSummary } from "./types.js";

export interface AppointmentBriefFact {
  key: QualificationKey;
  label: string;
  value: string | null;
}

export interface AppointmentBriefCheck {
  tone: "ready" | "attention" | "neutral";
  text: string;
}

export interface AppointmentBrief {
  facts: AppointmentBriefFact[];
  checks: AppointmentBriefCheck[];
  knownFactCount: number;
  missingFactLabels: string[];
  objective: string;
  ready: boolean;
}

type BriefRun = Pick<RunSummary, "acquisitionsOutcomeAt" | "appointmentAt" | "appointmentConfirmedAt" | "doNotContact" | "qualification" | "stage">;

const factLabels: Record<QualificationKey, string> = {
  motivation: "Why selling",
  timeline: "Timing",
  condition: "Condition",
  occupancy: "Occupancy",
  price: "Price expectation",
  decisionMakers: "Decision-makers"
};

const factKeys = Object.keys(factLabels) as QualificationKey[];
const finishedStages = new Set(["offer_made", "under_contract", "closed_not_fit", "closed", "do_not_contact"]);

export function appointmentBrief(run: BriefRun): AppointmentBrief | null {
  if (!run.appointmentAt || run.doNotContact || finishedStages.has(run.stage)) return null;
  if (run.acquisitionsOutcomeAt && new Date(run.appointmentAt).getTime() <= new Date(run.acquisitionsOutcomeAt).getTime()) return null;

  const facts = factKeys.map((key) => ({ key, label: factLabels[key], value: run.qualification[key] }));
  const missingFactLabels = facts.filter((fact) => !fact.value).map((fact) => fact.label);
  const checks: AppointmentBriefCheck[] = [run.appointmentConfirmedAt
    ? { tone: "ready", text: "Seller confirmed the appointment." }
    : { tone: "attention", text: "Seller confirmation is still pending." }];

  checks.push(missingFactLabels.length
    ? { tone: "attention", text: `Still learn: ${missingFactLabels.join(", ")}.` }
    : { tone: "ready", text: "All six seller facts are recorded. Verify them in person." });
  checks.push({ tone: "neutral", text: "Confirm everyone who can approve the sale will be present or reachable." });

  return {
    facts,
    checks,
    knownFactCount: facts.length - missingFactLabels.length,
    missingFactLabels,
    objective: "Verify the property condition and seller facts, answer questions, and agree on the next step.",
    ready: Boolean(run.appointmentConfirmedAt) && missingFactLabels.length === 0
  };
}
